/* eslint-disable react-refresh/only-export-components */
// Router files are intentionally mixed-export: lazy component imports alongside
// the router object itself. Fast-refresh doesn't apply here.
import { Suspense, useState, useEffect, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useMatch,
  useParams,
  useSearchParams,
  type RouteObject,
  type RouterProviderProps,
} from 'react-router-dom';
import { landingRedirect } from './shells/desktop/landing';
import { AppShellSkeleton, NoAccountFallback } from './components';
import { RequireAuth } from './components/RequireAuth';
import { KioskLockGuard } from './components/KioskLockGuard';
import { NotFound } from './shells/NotFound';
import { RouteError } from './shells/RouteError';
import { useUi } from './lib/uiStore';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './lib/store';
import { useSubscriptionStatus } from './lib/hooks/useSubscriptionStatus';
// `lazy` here is the stale-chunk-recovering wrapper: when a code-split chunk
// 404s after a deploy, it reloads once to fetch a fresh index.html + hashes
// rather than white-screening. See lib/lazyWithReload.
import { lazyWithReload as lazy } from './lib/lazyWithReload';

const SignInPage = lazy(() =>
  import('./features/auth/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('./features/auth/SignUpPage').then((m) => ({ default: m.SignUpPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./features/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const SubscribePage = lazy(() =>
  import('./features/subscribe/SubscribePage').then((m) => ({ default: m.SubscribePage })),
);
const PlanAndBillingPage = lazy(() =>
  import('./features/settings/PlanAndBillingPage').then((m) => ({
    default: m.PlanAndBillingPage,
  })),
);

const DesktopLayout = lazy(() =>
  import('./shells/desktop/DesktopLayout').then((m) => ({ default: m.DesktopLayout })),
);
const DesktopHome = lazy(() =>
  import('./shells/desktop/DesktopHome').then((m) => ({ default: m.DesktopHome })),
);
// vendor#1323. `/inventory` is a HUB over three types of stock now, with the
// cross-type search above them; the three legs are its children. The old
// grouped grid at `/inventory/grid` was REMOVED by owner ruling on 2026-09-22
// ("Yes remove the grid entirely", vendor#1327) — with it went the filter
// rail, the column menu, bulk edit, "select everything matching", CSV export
// and label printing, all five of which the owner had already cut. Do not
// rehome any of them onto a leg, and do not re-add a `?flag=` forward.
const InventoryHubPage = lazy(() =>
  import('./features/inventory/hub/InventoryHubPage').then((m) => ({
    default: m.InventoryHubPage,
  })),
);
const SinglesLegPage = lazy(() =>
  import('./features/inventory/legs/legPages').then((m) => ({ default: m.SinglesLegPage })),
);
const GradedLegPage = lazy(() =>
  import('./features/inventory/legs/legPages').then((m) => ({ default: m.GradedLegPage })),
);
const SealedLegPage = lazy(() =>
  import('./features/inventory/legs/legPages').then((m) => ({ default: m.SealedLegPage })),
);
// Three thin, prop-less route wrappers around the one shared
// AcquisitionsListPage (vendor#567, ruling R-T1 / decision D-T1) — the rail
// gets Sales, Buys and Trades in place of the old combined Acquisitions
// entry. `lazyWithReload` requires every code-split route component to be
// prop-less (see its docblock), so the shared list's `family` prop is fixed
// inside each wrapper rather than threaded through the lazy import.
const SalesListPage = lazy(() =>
  import('./features/acquisitions/AcquisitionsListPage').then((m) => ({
    default: m.SalesListPage,
  })),
);
const BuysListPage = lazy(() =>
  import('./features/acquisitions/AcquisitionsListPage').then((m) => ({
    default: m.BuysListPage,
  })),
);
const TradesListPage = lazy(() =>
  import('./features/acquisitions/AcquisitionsListPage').then((m) => ({
    default: m.TradesListPage,
  })),
);
const SaleIntakePage = lazy(() =>
  import('./features/sales/SaleIntakePage').then((m) => ({ default: m.SaleIntakePage })),
);
const SettingsHome = lazy(() =>
  import('./features/settings/SettingsHome').then((m) => ({ default: m.SettingsHome })),
);
const BarcodeSequencePage = lazy(() =>
  import('./features/settings/BarcodeSequencePage').then((m) => ({
    default: m.BarcodeSequencePage,
  })),
);
const PricingRulesPage = lazy(() =>
  import('./features/settings/PricingRulesPage').then((m) => ({
    default: m.PricingRulesPage,
  })),
);
const SlabLinksPage = lazy(() =>
  import('./features/settings/SlabLinksPage').then((m) => ({
    default: m.SlabLinksPage,
  })),
);
const GradingCostsPage = lazy(() =>
  import('./features/settings/GradingCostsPage').then((m) => ({
    default: m.GradingCostsPage,
  })),
);
const PsaSettingsPage = lazy(() =>
  import('./features/settings/PsaSettingsPage').then((m) => ({
    default: m.PsaSettingsPage,
  })),
);
const CardLadderSettingsPage = lazy(() =>
  import('./features/settings/CardLadderSettingsPage').then((m) => ({
    default: m.CardLadderSettingsPage,
  })),
);
const CardLadderBackfillPage = lazy(() =>
  import('./features/settings/CardLadderBackfillPage').then((m) => ({
    default: m.CardLadderBackfillPage,
  })),
);
const SpreadSettingsPage = lazy(() =>
  import('./features/settings/SpreadSettingsPage').then((m) => ({
    default: m.SpreadSettingsPage,
  })),
);
const SpreadExclusionsPage = lazy(() =>
  import('./features/analytics/exclusions/SpreadExclusionsPage').then((m) => ({
    default: m.SpreadExclusionsPage,
  })),
);
const KioskSettingsPage = lazy(() =>
  import('./features/settings/KioskSettingsPage').then((m) => ({
    default: m.KioskSettingsPage,
  })),
);
const WantListPage = lazy(() =>
  import('./features/settings/WantListPage').then((m) => ({
    default: m.WantListPage,
  })),
);
const KioskLeadsPage = lazy(() =>
  import('./features/settings/KioskLeadsPage').then((m) => ({
    default: m.KioskLeadsPage,
  })),
);
const AppearanceSettingsPage = lazy(() =>
  import('./features/settings/AppearanceSettingsPage').then((m) => ({
    default: m.AppearanceSettingsPage,
  })),
);
const YourPreferencesPage = lazy(() =>
  import('./features/settings/YourPreferencesPage').then((m) => ({
    default: m.YourPreferencesPage,
  })),
);
const TeamPage = lazy(() =>
  import('./features/settings/team/TeamPage').then((m) => ({ default: m.TeamPage })),
);
const InviteAcceptPage = lazy(() =>
  import('./features/invite/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage })),
);
const ContainersHomePage = lazy(() =>
  import('./features/containers/ContainersHomePage').then((m) => ({
    default: m.ContainersHomePage,
  })),
);
const BinsListPage = lazy(() =>
  import('./features/bins/BinsListPage').then((m) => ({ default: m.BinsListPage })),
);
const BinContentsPage = lazy(() =>
  import('./features/bins/BinContentsPage').then((m) => ({ default: m.BinContentsPage })),
);
// vendor#409 — the pricing lane. A sub-route of the bin rather than a mode on
// the contents page: a dealer 90 cards into 188 must survive a reload, and a
// URL is the only thing that survives one.
const PricingLanePage = lazy(() =>
  import('./features/bins/lane/PricingLanePage').then((m) => ({ default: m.PricingLanePage })),
);
const DisplayCasesListPage = lazy(() =>
  import('./features/display-cases/DisplayCasesListPage').then((m) => ({
    default: m.DisplayCasesListPage,
  })),
);
const DisplayCaseContentsPage = lazy(() =>
  import('./features/display-cases/DisplayCaseContentsPage').then((m) => ({
    default: m.DisplayCaseContentsPage,
  })),
);
const BindersListPage = lazy(() =>
  import('./features/binders/BindersListPage').then((m) => ({
    default: m.BindersListPage,
  })),
);
const BinderContentsPage = lazy(() =>
  import('./features/binders/BinderContentsPage').then((m) => ({
    default: m.BinderContentsPage,
  })),
);
const SealedStockListPage = lazy(() =>
  import('./features/sealed-stock/SealedStockListPage').then((m) => ({
    default: m.SealedStockListPage,
  })),
);
// Locate (vendor#352) and the kiosk preview (vendor#366). Both nest under
// `/containers` because both are about the containers as a set rather than
// about one of them, and both are STAFF-ONLY: `locate_card` returns owner
// identity and the preview writes the public switches, so neither may ever be
// reachable from the `(kiosk)` shell.
const LocatePage = lazy(() =>
  import('./features/containers/locate/LocatePage').then((m) => ({ default: m.LocatePage })),
);
const KioskPreviewPage = lazy(() =>
  import('./features/containers/kiosk-preview/KioskPreviewPage').then((m) => ({
    default: m.KioskPreviewPage,
  })),
);
// The Vault — the fifth container type (reinstated 2026-08-03). Its list route
// nests under `/containers` with the other four per `docs/redesign-rulings.md`
// Ruling 2 + Ruling 7; `docs/features/vault.md` §7.6 names `/containers/vault`
// explicitly. There is deliberately NO kiosk counterpart: the Vault must never
// reach the kiosk UI, kept out by the `features/vault/**` ESLint/Vitest import
// boundary — not by kiosk session anonymity, which isn't real (kiosk keeps
// the real staff session, vendor#742).
const VaultPage = lazy(() =>
  import('./features/vault/VaultPage').then((m) => ({ default: m.VaultPage })),
);
const HoldingDetailPage = lazy(() =>
  import('./features/vault/HoldingDetailPage').then((m) => ({ default: m.HoldingDetailPage })),
);
const AcquisitionDetailPage = lazy(() =>
  import('./features/acquisitions/AcquisitionDetailPage').then((m) => ({
    default: m.AcquisitionDetailPage,
  })),
);
const AcquisitionEditPage = lazy(() =>
  import('./features/acquisitions/AcquisitionEditPage').then((m) => ({
    default: m.AcquisitionEditPage,
  })),
);
const AcquisitionIntakePage = lazy(() =>
  import('./features/acquisitions/AcquisitionIntakePage').then((m) => ({
    default: m.AcquisitionIntakePage,
  })),
);
const BuyIntakePage = lazy(() =>
  import('./features/acquisitions/BuyIntakePage').then((m) => ({
    default: m.BuyIntakePage,
  })),
);
// The one-tap buy fork (vendor#249) — "a handful of cards" (BuyIntakePage,
// above) vs "a box, a binder, a collection" (LotBuyForm, below), plus the
// bulk-lot detail queue and its per-lot detail screen (vendor#254).
const BuyFork = lazy(() =>
  import('./features/acquisitions/BuyFork').then((m) => ({ default: m.BuyFork })),
);
const LotBuyForm = lazy(() =>
  import('./features/acquisitions/LotBuyForm').then((m) => ({ default: m.LotBuyForm })),
);
const LotDetailQueuePage = lazy(() =>
  import('./features/acquisitions/LotDetailQueuePage').then((m) => ({
    default: m.LotDetailQueuePage,
  })),
);
const LotDetailPage = lazy(() =>
  import('./features/acquisitions/LotDetailPage').then((m) => ({ default: m.LotDetailPage })),
);
const ParkedDraftsPage = lazy(() =>
  import('./features/acquisitions/ParkedDraftsPage').then((m) => ({
    default: m.ParkedDraftsPage,
  })),
);
const TradeIntakePage = lazy(() =>
  import('./features/acquisitions/TradeIntakePage').then((m) => ({
    default: m.TradeIntakePage,
  })),
);
const ExpensesPage = lazy(() =>
  import('./features/expenses/ExpensesPage').then((m) => ({ default: m.ExpensesPage })),
);
// Money surface (vendor#227) — settle-up, one member's ledger snapshot, and
// per-show P&L. Live under /expenses/* and /shows/:eventId/* rather than a
// new top-level nav item; see docs/multi-seat-accounts.md and
// mtb-design-system/templates/vendor-money/notes.md §4 for the placement
// reasoning.
const PayoutsPage = lazy(() =>
  import('./features/money/PayoutsPage').then((m) => ({ default: m.PayoutsPage })),
);
const SettleUpPage = lazy(() =>
  import('./features/money/SettleUpPage').then((m) => ({ default: m.SettleUpPage })),
);
const MemberLedgerPage = lazy(() =>
  import('./features/money/MemberLedgerPage').then((m) => ({ default: m.MemberLedgerPage })),
);
const MoneyPage = lazy(() =>
  import('./features/money/MoneyPage').then((m) => ({ default: m.MoneyPage })),
);
const ShowPnlPage = lazy(() =>
  import('./features/money/ShowPnlPage').then((m) => ({ default: m.ShowPnlPage })),
);
const ShowBagPage = lazy(() =>
  import('./features/money/ShowBagPage').then((m) => ({ default: m.ShowBagPage })),
);
const EventsPage = lazy(() =>
  import('./features/events/EventsPage').then((m) => ({ default: m.EventsPage })),
);
const ShowAddPage = lazy(() =>
  import('./features/events/ShowAddPage').then((m) => ({ default: m.ShowAddPage })),
);
const EventDetailPage = lazy(() =>
  import('./features/events/EventDetailPage').then((m) => ({ default: m.EventDetailPage })),
);
const EventsReportPage = lazy(() =>
  import('./features/events/EventsReportPage').then((m) => ({ default: m.EventsReportPage })),
);
const CardDetailPage = lazy(() =>
  import('./features/scanner/CardDetailPage').then((m) => ({ default: m.CardDetailPage })),
);
const ProvenancePage = lazy(() =>
  import('./features/provenance-tree/ProvenancePage').then((m) => ({
    default: m.ProvenancePage,
  })),
);
const ReportsHubPage = lazy(() =>
  import('./features/reports/ReportsHubPage').then((m) => ({ default: m.ReportsHubPage })),
);
const DowReportPage = lazy(() =>
  import('./features/reports/DowReportPage').then((m) => ({ default: m.DowReportPage })),
);
const HodReportPage = lazy(() =>
  import('./features/reports/HodReportPage').then((m) => ({ default: m.HodReportPage })),
);
const MomReportPage = lazy(() =>
  import('./features/reports/MomReportPage').then((m) => ({ default: m.MomReportPage })),
);
const TopMoversReportPage = lazy(() =>
  import('./features/reports/TopMoversReportPage').then((m) => ({
    default: m.TopMoversReportPage,
  })),
);
const PlSummaryPage = lazy(() =>
  import('./features/pl/PlSummaryPage').then((m) => ({ default: m.PlSummaryPage })),
);
const MemberPnlPage = lazy(() =>
  import('./features/pl/MemberPnlPage').then((m) => ({ default: m.MemberPnlPage })),
);
const UnrealizedPlPage = lazy(() =>
  import('./features/pl/UnrealizedPlPage').then((m) => ({ default: m.UnrealizedPlPage })),
);
const AnalyticsPage = lazy(() =>
  import('./features/analytics/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
const SpreadReportPage = lazy(() =>
  import('./features/analytics/spread/SpreadReportPage').then((m) => ({
    default: m.SpreadReportPage,
  })),
);
const HelpPage = lazy(() =>
  import('./features/help/HelpPage').then((m) => ({ default: m.HelpPage })),
);
const HelpArticlePage = lazy(() =>
  import('./features/help/HelpArticlePage').then((m) => ({ default: m.HelpArticlePage })),
);

// Dev-only: design system reference page. The lazy import lives inside the
// if-block so Vite's dead-code elimination strips the dynamic-import site
// from production builds when import.meta.env.DEV is false.
const devRoutes: RouteObject[] = [];
if (import.meta.env.DEV) {
  const DesignSystemPage = lazy(() =>
    import('./features/dev/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage })),
  );
  devRoutes.push({
    path: 'dev/design-system',
    element: withSuspense(<DesignSystemPage />),
  });
}

const KioskLayout = lazy(() =>
  import('./shells/kiosk/KioskLayout').then((m) => ({ default: m.KioskLayout })),
);
const KioskHome = lazy(() =>
  import('./shells/kiosk/KioskHome').then((m) => ({ default: m.KioskHome })),
);
const KioskDisplayCasesPage = lazy(() =>
  import('./features/kiosk/KioskDisplayCasesPage').then((m) => ({
    default: m.KioskDisplayCasesPage,
  })),
);
const KioskCaseDetailPage = lazy(() =>
  import('./features/kiosk/KioskCaseDetailPage').then((m) => ({ default: m.KioskCaseDetailPage })),
);
const KioskBindersPage = lazy(() =>
  import('./features/kiosk/KioskBindersPage').then((m) => ({ default: m.KioskBindersPage })),
);
const KioskBinderDetailPage = lazy(() =>
  import('./features/kiosk/KioskBinderDetailPage').then((m) => ({
    default: m.KioskBinderDetailPage,
  })),
);
// Slab Binders are a dropped container type (repo CLAUDE.md "Container
// taxonomy"), not a hidden one — the kiosk pages and route were dead code
// (route already commented out, unrouted) and are removed outright rather
// than kept commented, matching the kiosk design's own "no slab binders"
// divergence note (mtb-design-system/templates/vendor-kiosk/notes.md §15).
const KioskSealedPage = lazy(() =>
  import('./features/kiosk/KioskSealedPage').then((m) => ({ default: m.KioskSealedPage })),
);
const KioskDisplaySettingsPage = lazy(() =>
  import('./features/settings/KioskDisplaySettingsPage').then((m) => ({
    default: m.KioskDisplaySettingsPage,
  })),
);
const KioskFeaturedView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({
    default: m.KioskFeaturedView,
  })),
);
const KioskShowsView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskShowsView })),
);
const KioskConnectView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskConnectView })),
);
const KioskPayView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskPayView })),
);
const KioskJoinView = lazy(() =>
  import('./features/kiosk/display/KioskExtraViews').then((m) => ({ default: m.KioskJoinView })),
);
const KioskSearchPage = lazy(() =>
  import('./features/kiosk/KioskSearchPage').then((m) => ({ default: m.KioskSearchPage })),
);
const KioskWantedPage = lazy(() =>
  import('./features/kiosk/KioskWantedPage').then((m) => ({ default: m.KioskWantedPage })),
);
const KioskBulkBinsPage = lazy(() =>
  import('./features/kiosk/KioskBulkBinsPage').then((m) => ({ default: m.KioskBulkBinsPage })),
);
const KioskBulkBinDetailPage = lazy(() =>
  import('./features/kiosk/KioskBulkBinDetailPage').then((m) => ({
    default: m.KioskBulkBinDetailPage,
  })),
);

// vendor#137, final tranche. `shells/mobile/` is GONE — layout, bottom nav,
// home and the More launcher were pure phone chrome with a desktop equivalent
// each, so they were deleted rather than moved. Three pages were NOT chrome and
// are mounted below in the one authenticated tree, at the paths the design
// already assumed (`mtb-design-system/templates/vendor-graded/notes.md` §1:
// "no `scan`/`lookup`/`search`/`graded` route today, only nested under the
// still-fully-wired `/m` shell"):
//
//   MobileLookup      -> features/scanner/ScanPage        at /scan
//   MobileSearchPage  -> features/cardSearch/CardSearchPage at /search
//   MobileGradedPage  -> features/graded/GradedPage       at /graded
//                        (that page is retired; /graded is now the buy-decision
//                         lookup — see the GradedLookupPage import below)
//
// Each was the app's ONLY surface for what it does — camera scanning
// (`ScannerDrawer` is HID-only), catalog search (`useCardSearch` had one
// consumer), and cert entry (nothing else reaches `create_graded_instance`
// from a cert number). The SCREENS are unchanged; `vendor-scanner`,
// `vendor-scanner-intake` and `vendor-graded` conformance are separate passes.
const ScanPage = lazy(() =>
  import('./features/scanner/ScanPage').then((m) => ({ default: m.ScanPage })),
);
const CardSearchPage = lazy(() =>
  import('./features/cardSearch/CardSearchPage').then((m) => ({
    default: m.CardSearchPage,
  })),
);
// `/graded` is the BUY-DECISION lookup — a customer's slab, not ours (product
// owner's split, 2026-08-21; `mtb-design-system/templates/vendor-graded-buy`).
// It replaced `features/graded/GradedPage`, a container-less cert-entry form
// whose payoff was "add to inventory". Adding a slab you own happens from
// inside the container it is going into — the Graded tab of that container's
// Add-cards panel — so the old form was a duplicate of a flow that already
// exists in a better place, and its add wrote a cost basis of ZERO, which
// reports the whole sale price as profit.
const GradedLookupPage = lazy(() =>
  import('./features/graded/lookup/GradedLookupPage').then((m) => ({
    default: m.GradedLookupPage,
  })),
);
// vendor#1188 (epic vendor#1159) — Submissions: research a raw card's grading
// verdict, build a shortlist, track what is out. Grouped with Graded/Search in
// the Tools nav group — see `shells/desktop/navConfig.tsx`.
const SubmissionsHubPage = lazy(() =>
  import('./features/submissions/SubmissionsHubPage').then((m) => ({
    default: m.SubmissionsHubPage,
  })),
);
const SubmissionBuilderPage = lazy(() =>
  import('./features/submissions/SubmissionBuilderPage').then((m) => ({
    default: m.SubmissionBuilderPage,
  })),
);
const SubmissionDetailPage = lazy(() =>
  import('./features/submissions/SubmissionDetailPage').then((m) => ({
    default: m.SubmissionDetailPage,
  })),
);
const SubmissionsResearchPage = lazy(() =>
  import('./features/submissions/SubmissionsResearchPage').then((m) => ({
    default: m.SubmissionsResearchPage,
  })),
);
// RETIRED (vendor#384, under vendor#137). `MobileEditPage` is gone; the
// `/m/edit/:barcode` twin now resolves its barcode to an instance and lands
// on the responsive editor at `/instances/:instanceId/edit`.
const BarcodeEditRedirect = lazy(() =>
  import('./features/scanner/BarcodeEditRedirect').then((m) => ({
    default: m.BarcodeEditRedirect,
  })),
);

// Prototype routes — only registered when VITE_SHOW_PROTOTYPES=true. The lazy
// imports live inside the if-block so Vite's dead-code elimination strips the
// dynamic-import sites from prod builds when the env var is unset.
// See docs/spikes/01-4-ux.md.
const SHOW_PROTOTYPES = import.meta.env.VITE_SHOW_PROTOTYPES === 'true';

// vendor#137: ONE list, mounted under `/`. There were two — `prototypeRoutes`
// under `/m` (all three) and `desktopPrototypeRoutes` under `/` (scanner-demo
// only), so `proto/scanner-demo` was registered twice at two URLs. With the
// `/m` tree gone the split has nothing left to express; merging them is what
// stops `scan-to-add` and `scan-to-find` becoming unreachable. Still gated on
// `VITE_SHOW_PROTOTYPES`, so prod builds strip all of it.
const prototypeRoutes: RouteObject[] = [];
if (SHOW_PROTOTYPES) {
  const ScanToAddPrototype = lazy(() =>
    import('./prototypes/m/scan-to-add').then((m) => ({ default: m.ScanToAddPrototype })),
  );
  const ScanToFindPrototype = lazy(() =>
    import('./prototypes/m/scan-to-find').then((m) => ({ default: m.ScanToFindPrototype })),
  );
  const ScannerDemoPrototype = lazy(() =>
    import('./prototypes/scanner-demo').then((m) => ({ default: m.ScannerDemoPrototype })),
  );
  prototypeRoutes.push(
    { path: 'proto/scan-to-add', element: withSuspense(<ScanToAddPrototype />) },
    { path: 'proto/scan-to-find', element: withSuspense(<ScanToFindPrototype />) },
    { path: 'proto/scanner-demo', element: withSuspense(<ScannerDemoPrototype />) },
  );
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<AppShellSkeleton />}>{node}</Suspense>;
}

// `ownerOnly` (wrapping a route in `RequireRole` / `ForbiddenPage`) was
// removed 2026-08 when the five settings routes it gated moved their
// owner-check inside the page itself (`SettingsPageShell`'s `locked` prop +
// `OwnerOnlyPage`) — see the settings route registrations below. `RequireRole`
// and `ForbiddenPage` are still real components (other non-settings surfaces
// may still want a hard route gate); this file just no longer has a call site
// for either.

/**
 * Subscription gate for the staff app (desktop `/` and mobile `/m`).
 *
 * Vendor billing is binary: an account may use the app only while its tier
 * allows writes AND its subscription is active/trialing (see
 * {@link useSubscriptionStatus}). Unsubscribed accounts are redirected to
 * `/subscribe` — the storefront-style "no access until paid" gate. The kiosk
 * (`/kiosk`) is intentionally NOT wrapped in this gate — not because its
 * session is anonymous (it isn't: kiosk keeps the real staff session,
 * vendor#742), but because reaching kiosk already required passing this gate
 * once, from the staff side, before `EnterKioskModal` was even reachable.
 *
 * Returning from Stripe Checkout (`?subscribed=1`), the webhook that flips the
 * account to the paid tier is async, so we poll the subscription status every
 * 2 s (up to ~30 s) and land the user in the app once writes unlock — rather
 * than bouncing them back to /subscribe before the webhook fires.
 *
 * vendor#157 item 4: an authenticated session with no current account
 * (`currentAccountId === null`) used to fall into the same branch as "still
 * resolving" below and sit on `<AppShellSkeleton />` forever — indistinguishable
 * from loading, with no exit. That branch is now split out: once the session
 * itself has resolved, a null `currentAccountId` renders
 * {@link NoAccountFallback} (a real exit) instead of waiting on data that will
 * never arrive, because there is no account id to fetch a subscription row
 * for.
 *
 * **Exported (vendor#618) so it can be rendered directly in a test**, the
 * same way `RequireAuth`/`RequireRole` already are — see
 * `apps/web/src/__tests__/RequireSubscription.test.tsx`, which pins the
 * invariant below.
 *
 * **Corrected 2026-08-11 (vendor#635).** This block used to claim the
 * `!currentAccountId` check "MUST run before" the `subLoading` check, or a
 * resolved-but-accountless session would fall through to
 * `<Navigate to="/subscribe" />`. That does not follow: `useSubscriptionStatus`
 * is `enabled: Boolean(accountId)`, so with no account id `subLoading` is
 * `false`, not `true` — control reaches the `!currentAccountId` check either
 * way, regardless of which of the two runs first. **The invariant is that the
 * two checks are SEPARATE, not that they are ordered.** The bug this guards
 * against predates vendor#616's split: the combined
 * `sessionStatus === 'loading' || !currentAccountId || subLoading` check gave
 * an accountless session a skeleton with no exit, because `!currentAccountId`
 * was folded into the same branch as "still loading" instead of getting its
 * own. Splitting it out — not the order of the split branches — is what
 * fixed it.
 *
 * **vendor#619 — the over-seat carve-out.** `writesAllowed` folds in the
 * seats arm (`useSubscriptionStatus`'s docblock), so an account Stripe
 * downgraded below its member count now redirects here like any other
 * unsubscribed account — which is new: it used to read as `writesAllowed:
 * true` and pass straight through. `/subscribe` explains that state and
 * links to Settings › Team so the owner can remove a member — but Team lives
 * under this very gate (`router.tsx`'s `settings/team` route, nested under
 * `/`), so without an exception the fix would immediately redirect back to
 * `/subscribe`, a dead end. The carve-out is narrow and pathname-scoped: it
 * only ever fires when the SUBSCRIPTION itself is live
 * (`sub.subscriptionLive`) and only the seat count is over
 * (`sub.seatsOverLimit`) — a genuinely lapsed/past_due account still
 * redirects unconditionally, on every path, because paying (not removing a
 * member) is its remedy and Team is not where that happens.
 */
export function RequireSubscription({ children }: { children: ReactNode }) {
  const currentAccountId = useSession((s) => s.currentAccountId);
  const sessionStatus = useSession((s) => s.status);
  // `useMatch` rather than a literal `location.pathname === '/settings/team'`
  // compare (code review, PR #789 N1) — the literal fails CLOSED (a
  // case/trailing-slash mismatch just means the carve-out never fires and
  // Team redirects like any other route, not a security hole), but
  // `useMatch` is the correct primitive for "is this route active" and
  // matches how the rest of the router expresses path membership.
  const isSettingsTeamRoute = useMatch('/settings/team');
  const [searchParams, setSearchParams] = useSearchParams();
  const subscribed = searchParams.get('subscribed') === '1';
  const [timedOut, setTimedOut] = useState(false);
  const queryClient = useQueryClient();

  const { data: sub, isLoading: subLoading } = useSubscriptionStatus(currentAccountId);
  const writesAllowed = sub?.writesAllowed ?? false;
  const seatsOverLimitCarveOut =
    (sub?.subscriptionLive ?? false) &&
    (sub?.seatsOverLimit ?? false) &&
    isSettingsTeamRoute != null;

  // Poll the subscription status while waiting for the Stripe webhook to flip
  // the account onto the paid tier after checkout.
  useEffect(() => {
    if (!subscribed || writesAllowed || timedOut || !currentAccountId) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts++;
      void queryClient.invalidateQueries({
        queryKey: ['subscription-status', currentAccountId],
      });
      if (attempts >= 15) {
        clearInterval(id);
        setTimedOut(true);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [subscribed, writesAllowed, timedOut, currentAccountId, queryClient]);

  // Strip ?subscribed=1 from the URL once the subscription is confirmed active.
  useEffect(() => {
    if (subscribed && writesAllowed) {
      setSearchParams(
        (p) => {
          p.delete('subscribed');
          return p;
        },
        { replace: true },
      );
    }
  }, [subscribed, writesAllowed, setSearchParams]);

  // Still resolving the session → skeleton.
  if (sessionStatus === 'loading') {
    return <AppShellSkeleton />;
  }

  // Authenticated, but no current account at all — a real exit, not an
  // infinite skeleton (vendor#157 item 4). Must be checked before the
  // subLoading branch below and separately from it: `useSubscriptionStatus`
  // is `enabled: Boolean(accountId)`, so with no account id there is no
  // query to resolve — the old combined check (`!currentAccountId ||
  // subLoading`) rendered the same skeleton either way and gave this case no
  // path out.
  if (!currentAccountId) {
    return <NoAccountFallback />;
  }

  // Still resolving the subscription row → skeleton.
  if (subLoading) {
    return <AppShellSkeleton />;
  }

  if (writesAllowed || seatsOverLimitCarveOut) return <>{children}</>;

  // Not subscribed. While the post-checkout webhook is landing, hold a skeleton;
  // if it never arrives, surface a recovery message.
  if (subscribed && !timedOut) return <AppShellSkeleton />;
  if (subscribed && timedOut) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
          <p style={{ marginBottom: 8, fontWeight: 600 }}>Activating your subscription…</p>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
            Your payment was received. This can take a moment. Please refresh, or contact support if
            this persists.
          </p>
          <button
            onClick={() => window.location.replace('/?subscribed=1')}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              border: '1px solid #ccc',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }
  return <Navigate to="/subscribe" replace />;
}

/**
 * Desktop index route (the app entry at `/`).
 *
 * Honors the user's `defaultLandingPath` preference: the dashboard (`/`)
 * renders inline; any other choice issues a one-hop redirect — on opening the
 * app. A navigation carrying `DASHBOARD_STATE` (the phone's Home tab,
 * vendor#1409) renders the dashboard regardless. The allow-list in
 * `uiStore` guarantees the target is a real route, and its persist `merge`
 * re-validates the rehydrated value, so this can never loop or 404 — including
 * for a device that had already chosen `/alerts` before #504 removed it.
 *
 * **The phone redirect is gone — vendor#137.** This used to read:
 *
 * ```ts
 * if (isPhone && appMode === 'show') return <Navigate to="/m/home" replace />;
 * ```
 *
 * which sent a phone into the `/m/*` shell whenever the persisted
 * `useUi().appMode` said `'show'`. That was the mechanism by which anyone ever
 * arrived in the mobile shell without typing its URL, and for most of the
 * feature's life it fired on a *fresh* visit too, because `uiStore` shipped
 * `'show'` as the default — which made the redesigned responsive dashboard
 * unreachable on a phone.
 *
 * The product owner retired the split on 2026-08-03 (ratified 2026-08-04):
 * one responsive authenticated app, one route set, show mode as a subpage
 * within the show rather than a persisted mode. With no mode to read, there is
 * nothing for this branch to decide — `defaultLandingPath` is now the only
 * landing mechanism, on every device.
 *
 * `/m/*` itself is still mounted below and still reachable by URL. That is
 * deliberate and temporary: vendor is live, the routes are bookmarked on booth
 * phones, and each one goes only when its responsive replacement ships
 * (`docs/redesign-rulings.md` §1.3 has the map). What this change removes is
 * the app *sending* anyone there.
 */
function DesktopIndex() {
  const landing = useUi((s) => s.defaultLandingPath);
  // The phone's Home tab asks for the dashboard itself; the landing
  // preference is for opening the app (vendor#1409, `shells/desktop/landing.ts`).
  const location = useLocation();
  const redirect = landingRedirect(landing, location.state);
  if (redirect) return <Navigate to={redirect} replace />;
  return <DesktopHome />;
}

/**
 * Redirects /find (and /find?q=term) to /inventory (and /inventory?q=term).
 * Preserves the ?q= param so Spotlight deep-links and any existing bookmarks
 * continue to work after the two pages were merged.
 */
function FindRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/inventory${search}`} replace />;
}

/**
 * Redirects the legacy `/events/:eventId` path to `/shows/:eventId`.
 *
 * Shows conformance (`mtb-design-system/templates/vendor-shows/notes.md`
 * §1): the user-facing route is `/shows` — `events` stays the table name,
 * only the URL moved. Vendor is live and `/events/*` bookmarks are real, so
 * every old path redirects rather than 404ing.
 */
function EventDetailRedirect() {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/shows/${eventId ?? ''}`} replace />;
}

/**
 * Redirects the legacy `/sales/:saleId` path to `/acquisitions/:acquisitionId`.
 *
 * vendor#567 (ruling R-T1, decision D-T3): the detail screen has exactly one
 * canonical URL regardless of which rail entry a row was opened from — do
 * not add a `/buys/:id` or `/trades/:id` twin either. `/sales/:saleId`
 * predates the split and was already unused in-app
 * (`docs/link-topology.md` listed it ORPHANED); this keeps any external
 * bookmark working rather than 404ing, same reasoning as `/events/:eventId`
 * above.
 */
function SaleDetailRedirect() {
  const { saleId } = useParams<{ saleId: string }>();
  return <Navigate to={`/acquisitions/${saleId ?? ''}`} replace />;
}

/**
 * Redirects the legacy `/events/:eventId/show` AND `/shows/:eventId/show`
 * paths to `/shows/:eventId`. Show mode is no longer a separate subpage — it
 * is the Live mode of the single event-detail screen (see `router.tsx`'s
 * route table, `EventDetailPage`'s docblock, and `docs/redesign-rulings.md`
 * Ruling 5). Both redirects share this component since they now have the
 * same destination.
 */
function ShowModeRedirect() {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/shows/${eventId ?? ''}`} replace />;
}

/**
 * Redirects the retired `/m/expenses` twin to `/expenses`, preserving the query
 * string so `?event=<uuid>` survives.
 *
 * vendor#137 retires `/m/*` one twin at a time, as each responsive replacement
 * ships; vendor#227 shipped this one. `<Navigate to="/expenses">` on its own
 * would DROP the search, silently turning "expenses for the Columbus show" into
 * "all expenses" — the same class of quiet mis-wire the link-topology rule
 * exists to catch. Bookmarks are real (vendor is live), so this redirects
 * rather than 404s.
 */
function MobileExpensesRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/expenses${search}`} replace />;
}

/**
 * Redirects the retired phone show hub `/m/show/:eventId` to `/shows/:eventId`.
 *
 * vendor#137. The hub's replacement is not a port — `EventDetailPage` already
 * renders `EventDetailLive` for an active show, which IS the booth screen
 * (running tally, Sell/Buy/Trade, intake bin) and is responsive down to a
 * phone. There was nothing left for a second hub to do.
 */
function MobileShowRedirect() {
  const { eventId } = useParams();
  return <Navigate to={eventId ? `/shows/${eventId}` : '/shows'} replace />;
}

/**
 * Redirects a retired booth flow to its regular intake form, carrying the show.
 *
 * vendor#137, owner ruling 2026-08-18: *"we go regular sale and add the ability
 * for offline sales is the right thing to do."* `MobileQuickSell`/`Buy`/`Trade`
 * are deleted rather than ported, because the one thing they did that the
 * regular forms could not — record with no signal — the regular forms now do.
 *
 * `?eventId=` is preserved because it is the whole reason the `/m/show/:eventId/…`
 * variants existed: a sale taken at a booth has to land against that show. All
 * three destinations read the parameter (`SaleIntakePage`, `BuyIntakePage` via
 * `initialEventId`, `TradeIntakePage`), so a bookmarked booth link still files
 * its money in the right place.
 */
function MobileBoothRedirect({ to }: { to: string }) {
  const { eventId } = useParams();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (eventId) params.set('eventId', eventId);
  const query = params.toString();
  return <Navigate to={query ? `${to}?${query}` : to} replace />;
}

/**
 * Redirects the retired `/m/expenses/new` and `/m/expenses/:expenseId/edit`
 * twins to `/expenses`, carrying `?new=1` so the add sheet opens on arrival —
 * which is what those two paths did.
 *
 * The edit twin cannot restore its own row (the responsive screen opens the
 * detail sheet from a row click, not from a URL), so it lands on the list with
 * the add sheet available rather than 404ing.
 */
function MobileExpenseFormRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('new', '1');
  return <Navigate to={`/expenses?${params.toString()}`} replace />;
}

export const router: RouterProviderProps['router'] = createBrowserRouter([
  // ----- public auth surfaces (no RequireAuth gate) ------------------------
  { path: '/sign-in', element: withSuspense(<SignInPage />) },
  { path: '/sign-up', element: withSuspense(<SignUpPage />) },
  { path: '/reset-password', element: withSuspense(<ResetPasswordPage />) },
  // Every staff route below is wrapped in KioskLockGuard (vendor#1420): while
  // kiosk mode is active it redirects to /kiosk, so the PIN exit is the only
  // way back in. The three public auth routes above are NOT, deliberately —
  // see KioskLockGuard's docblock: they show no staff data, and they are how a
  // locked tablet whose staff session died gets back to a working PIN exit.
  {
    path: '/subscribe',
    element: (
      <KioskLockGuard>
        <RequireAuth>{withSuspense(<SubscribePage />)}</RequireAuth>
      </KioskLockGuard>
    ),
  },
  // NOT wrapped in RequireAuth, deliberately (vendor#59): the accept screen's
  // own "not signed in" state IS the unauthenticated render of this route —
  // RequireAuth would redirect away before that state ever had a chance to
  // render. Because of that, InviteAcceptPage calls useAuthBootstrap() itself
  // (RequireAuth is otherwise the only call site in the app) and the token
  // survives the sign-in/sign-up round trip via `?next=` alone — see
  // InviteAcceptPage's docblock.
  //
  // Kiosk-locked all the same: accepting an invite joins the signed-in staff
  // user to an account, which is a staff action and needs the PIN first.
  {
    path: '/invite/:token',
    element: <KioskLockGuard>{withSuspense(<InviteAcceptPage />)}</KioskLockGuard>,
  },

  {
    path: '/',
    // KioskLockGuard is OUTSIDE RequireAuth on purpose: a locked device never
    // bootstraps or paints any part of the staff shell (vendor#1420).
    element: (
      <KioskLockGuard>
        <RequireAuth>
          <RequireSubscription>
            <Suspense fallback={<AppShellSkeleton />}>
              <DesktopLayout />
            </Suspense>
          </RequireSubscription>
        </RequireAuth>
      </KioskLockGuard>
    ),
    // The OUTER boundary, and it is still necessary after vendor#586: this one
    // catches anything thrown by RequireAuth, RequireSubscription or
    // DesktopLayout itself — the cases where there is no shell left to
    // preserve, because the shell is what failed. Child-route errors no longer
    // reach it; they stop at the pathless boundary below.
    errorElement: <NotFound />,
    children: [
      {
        // vendor#586. A PATHLESS layout route whose only job is to carry an
        // errorElement one level below the shell.
        //
        // React Router replaces the ERRORING route's `element` with its
        // `errorElement`, and an error bubbles to the nearest ancestor that
        // declares one. With the root route the only declarer, a render error
        // in any child — a stale chunk, a bad `.data()` call, a component bug
        // on one report page — unmounted `DesktopLayout` and replaced the whole
        // screen. The user lost the nav rail, the header and every other way
        // out, which is why vendor#585's dead end was reachable at all.
        //
        // Pathless, so it adds no segment and no URL changes. One boundary
        // rather than one per top-level section: per-section would be the same
        // component mounted N times with no per-section behaviour, which is an
        // invented distinction and N places to keep in step.
        errorElement: <RouteError />,
        children: [
          { index: true, element: withSuspense(<DesktopIndex />) },
          { path: 'inventory', element: withSuspense(<InventoryHubPage />) },
          { path: 'inventory/singles', element: withSuspense(<SinglesLegPage />) },
          { path: 'inventory/graded', element: withSuspense(<GradedLegPage />) },
          { path: 'inventory/sealed', element: withSuspense(<SealedLegPage />) },
          // There is no `inventory/grid`. The grouped grid was removed whole by
          // owner ruling (vendor#1327, 2026-09-22) — route, component tree and
          // the `?flag=` forward that was its last inbound reference. Nothing
          // redirects, because the surface it would redirect TO does not exist.
          // vendor#137. The three surfaces that came out of `shells/mobile/` and
          // had nowhere else to be. Phone-first screens on the one route set —
          // "mobile-first is a responsiveness property of each screen, not a
          // parallel tree" (repo CLAUDE.md, Shells). `/scan` and `/graded` carry a
          // rail entry each in `shells/desktop/navConfig.tsx`, which also gives
          // each a Spotlight action for free — `GlobalSpotlight` builds its Pages
          // group from that same list.
          { path: 'scan', element: withSuspense(<ScanPage />) },
          { path: 'search', element: withSuspense(<CardSearchPage />) },
          { path: 'graded', element: withSuspense(<GradedLookupPage />) },
          // Submissions (vendor#1188/#1159) — same Tools placement as
          // Graded/Search above: "help you find or grade a card".
          { path: 'submissions', element: withSuspense(<SubmissionsHubPage />) },
          { path: 'submissions/research', element: withSuspense(<SubmissionsResearchPage />) },
          { path: 'submissions/new', element: withSuspense(<SubmissionBuilderPage />) },
          { path: 'submissions/:id', element: withSuspense(<SubmissionDetailPage />) },
          { path: 'sales', element: withSuspense(<SalesListPage />) },
          { path: 'sales/new', element: withSuspense(<SaleIntakePage />) },
          { path: 'sales/:saleId', element: <SaleDetailRedirect /> },
          // vendor#567 (ruling R-T1) — the rail's Buys and Trades entries. Both
          // render the same shared AcquisitionsListPage as /sales above, locked
          // to their own family; detail still opens at the one canonical
          // `/acquisitions/:acquisitionId` below.
          { path: 'buys', element: withSuspense(<BuysListPage />) },
          // Internal team purchase — REMOVED, not deprecated (product owner
          // ruling 2026-08-24: "it is causing way more trouble than it is
          // honestly worth as a feature"). `TeamBuyPage`/`TeamRequestsPanel`/
          // `useInternalPurchase`/`teamBuyPricing` are deleted. The three old
          // paths (the pre-merge pair plus the merged page itself) all redirect
          // to `/buys` rather than 404 — vendor is live and each was a
          // bookmarked rail entry at some point.
          { path: 'buys/teammate', element: <Navigate to="/buys" replace /> },
          { path: 'buys/from-teammate', element: <Navigate to="/buys" replace /> },
          { path: 'buys/requests', element: <Navigate to="/buys" replace /> },
          { path: 'trades', element: withSuspense(<TradesListPage />) },
          // Containers hub (#318/#221) — the shelf. The four list routes below
          // re-nest under it (`docs/redesign-rulings.md` Ruling 7); the contents
          // (detail) routes stay at their existing top-level paths — Ruling 7
          // names only the four LIST routes, and `BinsListPage`/
          // `DisplayCasesListPage`/`BindersListPage` already navigate to those
          // paths internally, so leaving them in place keeps this re-nest to
          // exactly what was ratified.
          {
            path: 'containers',
            element: <Outlet />,
            children: [
              { index: true, element: withSuspense(<ContainersHomePage />) },
              { path: 'bins', element: withSuspense(<BinsListPage />) },
              { path: 'cases', element: withSuspense(<DisplayCasesListPage />) },
              { path: 'binders', element: withSuspense(<BindersListPage />) },
              { path: 'sealed-stock', element: withSuspense(<SealedStockListPage />) },
              // Not type pages: both are about the containers as a set. `locate`
              // is reached from the hub's top-bar button (desktop) and its phone
              // header icon; `kiosk-preview` from the hub's "Preview kiosk"
              // button. Both entry points are wired in `ContainersHomePage` — see
              // `docs/link-topology.md`.
              { path: 'locate', element: withSuspense(<LocatePage />) },
              { path: 'kiosk-preview', element: withSuspense(<KioskPreviewPage />) },
              // The Vault's list, and one holding's detail. The detail nests under
              // the list (rather than reusing `/instances/:instanceId`) because a
              // vault holding is not just a card — it carries a target, a note and
              // a distance that only mean anything inside the vault. `:holdingKey`
              // is `i-<instance_id>` or `s-<sealed_stock_id>`: a sealed holding has
              // no instance id, so neither id alone identifies every holding.
              { path: 'vault', element: withSuspense(<VaultPage />) },
              { path: 'vault/:holdingKey', element: withSuspense(<HoldingDetailPage />) },
            ],
          },
          // Legacy top-level paths — vendor is live and these have been the URLs
          // since Phase 1, so every one redirects rather than 404ing (same
          // argument as the `/events/*` redirects below).
          { path: 'bins', element: <Navigate to="/containers/bins" replace /> },
          { path: 'cases', element: <Navigate to="/containers/cases" replace /> },
          { path: 'binders', element: <Navigate to="/containers/binders" replace /> },
          { path: 'sealed-stock', element: <Navigate to="/containers/sealed-stock" replace /> },
          // `/vault` has never been a live URL in this app, so this is not a
          // legacy-bookmark redirect like the four above. It exists because the
          // design template, `docs/features/vault.md` and
          // `mtb-design-system/templates/vendor-vault/nav.js` all name `/vault`,
          // and a reader following any of them should land on the screen rather
          // than on a 404. Canonical path stays `/containers/vault`.
          { path: 'vault', element: <Navigate to="/containers/vault" replace /> },
          { path: 'bins/:binId/contents', element: withSuspense(<BinContentsPage />) },
          { path: 'bins/:binId/price', element: withSuspense(<PricingLanePage />) },
          {
            path: 'cases/:caseId/contents',
            element: withSuspense(<DisplayCaseContentsPage />),
          },
          {
            path: 'binders/:binderId/contents',
            element: withSuspense(<BinderContentsPage />),
          },
          // vendor#567 (ruling R-T1) — the rail entry is gone (Sales/Buys/Trades
          // replace it), but the URL stays for bookmarks and the ~20 contextual
          // links that still point at `/acquisitions`. `/buys` is the
          // closest-meaning target: "acquisitions" historically meant things
          // acquired. Same shape as `/events` -> `/shows` below.
          { path: 'acquisitions', element: <Navigate to="/buys" replace /> },
          { path: 'acquisitions/new', element: withSuspense(<AcquisitionIntakePage />) },
          // vendor#249: the fork replaces the direct card-by-card landing — "a
          // handful of cards" now lives one tap deeper at .../buy/cards.
          { path: 'acquisitions/new/buy', element: withSuspense(<BuyFork />) },
          { path: 'acquisitions/new/buy/cards', element: withSuspense(<BuyIntakePage />) },
          { path: 'acquisitions/new/buy/lot', element: withSuspense(<LotBuyForm />) },
          { path: 'acquisitions/lots', element: withSuspense(<LotDetailQueuePage />) },
          { path: 'acquisitions/lots/:acquisitionId', element: withSuspense(<LotDetailPage />) },
          { path: 'acquisitions/drafts', element: withSuspense(<ParkedDraftsPage />) },
          { path: 'acquisitions/new/trade', element: withSuspense(<TradeIntakePage />) },
          { path: 'acquisitions/:acquisitionId', element: withSuspense(<AcquisitionDetailPage />) },
          // vendor#1050. The record's Edit button opens this rather than the
          // three-field modal it used to. Nested under the canonical detail URL
          // on purpose: there is one detail route for all four kinds (D-T3), so
          // there is one edit route — no `/trades/:id/edit` twin.
          {
            path: 'acquisitions/:acquisitionId/edit',
            element: withSuspense(<AcquisitionEditPage />),
          },
          { path: 'find', element: <FindRedirect /> },
          { path: 'instances/:instanceId', element: withSuspense(<CardDetailPage />) },
          // Edit is a MODE on the same pane, but a REAL URL (vendor#384,
          // `notes.md` #4: "linkable, back-navigable"). Keyed on the instance,
          // not the design's `edit/:barcode` — `docs/redesign-rulings.md` §1.3
          // ports it because `barcode` is nullable and lazily allocated, so a
          // never-labelled card has no key under that scheme.
          { path: 'instances/:instanceId/edit', element: withSuspense(<CardDetailPage edit />) },
          { path: 'provenance/:instanceId', element: withSuspense(<ProvenancePage />) },
          { path: 'reports', element: withSuspense(<ReportsHubPage />) },
          { path: 'reports/dow', element: withSuspense(<DowReportPage />) },
          { path: 'reports/hod', element: withSuspense(<HodReportPage />) },
          { path: 'reports/mom', element: withSuspense(<MomReportPage />) },
          { path: 'reports/top-movers', element: withSuspense(<TopMoversReportPage />) },
          { path: 'reports/pl', element: withSuspense(<PlSummaryPage />) },
          { path: 'reports/unrealized', element: withSuspense(<UnrealizedPlPage />) },
          // The OWNER DIMENSION of /reports/pl (vendor#126, settled 2026-08-04):
          // "how much has each of us made over this window". Its per-EVENT twin
          // lives with the show at /shows/:eventId/pnl. Reachable from both places
          // on purpose — one figure, two questions.
          { path: 'reports/pl/by-member', element: withSuspense(<MemberPnlPage />) },
          { path: 'pl', element: <Navigate to="/reports/pl" replace /> },
          { path: 'pl/unrealized', element: <Navigate to="/reports/unrealized" replace /> },
          // Deliberately NOT owner-gated — an operator sees the same cost basis,
          // margin and "money asleep" figures the owner does. `settings/billing`
          // and `settings/barcode`, further down this file, DO lock their content
          // for non-owners via `SettingsPageShell`'s `locked` prop; `settings/
          // pricing` does NOT (`locked={false}` — operators may write pricing
          // rules, a day-to-day surface, not a billing one). This route matches
          // neither pattern by ruling, not by oversight: `docs/redesign-
          // rulings.md` "R-A1 · Card analytics stays visible to every account
          // member". Do not add a guard here without re-opening that ruling first.
          { path: 'analytics', element: withSuspense(<AnalyticsPage />) },
          // The sticker-spread worklist (vendor#898, owner ruling 2026-08-15).
          // Two design templates disagreed about whether this is a section of
          // `/analytics` or its own surface; the later one — `templates/vendor-
          // analytics/notes.md` §3, which cuts it off the page and draws a link
          // here from `HoldingsTail` — won. `SpreadWorklist` itself is unchanged;
          // only its mount point moved. Inherits the same "visible to every
          // account member" ruling as `analytics` above.
          { path: 'analytics/spread', element: withSuspense(<SpreadReportPage />) },
          // Exclusions list (vendor#399, #182 C) — reached from the "Review
          // exclusions" link in the redesigned worklist's populations panel
          // (vendor#393, features/analytics/spread/components/spreadParts.tsx;
          // previously the "Excluded (N)" link in the old StickerDriftSection
          // table, which #393 replaced in place). Design source:
          // mtb-design-system/templates/vendor-spread-notes/exclude.js
          // `ExclusionsList`; no route was assigned by the design itself
          // (#225's own body: "designed, no route assigned").
          { path: 'analytics/exclusions', element: withSuspense(<SpreadExclusionsPage />) },
          // vendor#1096 — the way into the money surface. The 2026-09-01 design
          // adds this hub above the four existing screens and keeps their
          // routes where they are, so `/money` is new and `/expenses/*` is
          // unchanged. `MoneyNav` links all five.
          { path: 'money', element: withSuspense(<MoneyPage />) },
          { path: 'expenses', element: withSuspense(<ExpensesPage />) },
          // Money surface (vendor#227) — see the lazy-import comment above.
          { path: 'expenses/settle-up', element: withSuspense(<SettleUpPage />) },
          // vendor#1010. The account-wide record of every handover — the
          // question "what have we actually paid out", as against the ledger
          // card's "how do I stand with this one person". Both read
          // get_member_payout_status, so they cannot disagree.
          { path: 'expenses/payouts', element: withSuspense(<PayoutsPage />) },
          { path: 'expenses/ledger/:userId', element: withSuspense(<MemberLedgerPage />) },
          // vendor#267's chronological twin STOOD HERE and was deleted in
          // vendor#1096. The 2026-09-01 money design reworks the ledger into
          // three cards that deliberately stop restating "the payout list, the
          // settle-up working, and the show breakdown" — the timeline was that
          // show breakdown, and the redesigned ledger has no link to it, so
          // keeping the route would have left a page reachable only by URL.
          // Owner's call, 2026-09-01: "delete it".
          // Shows — the user-facing route (mtb-design-system/templates/vendor-shows
          // /notes.md §1: "the user-facing word is show"; `events` stays the
          // table name, only the route moved).
          { path: 'shows', element: withSuspense(<EventsPage />) },
          { path: 'shows/new', element: withSuspense(<ShowAddPage />) },
          { path: 'shows/:eventId', element: withSuspense(<EventDetailPage />) },
          // Per-member show P&L (vendor#227/#259) — see the lazy-import comment above.
          { path: 'shows/:eventId/pnl', element: withSuspense(<ShowPnlPage />) },
          // The bag (#1086) — what the show is holding and who is owed what out
          // of it. A screen of its own in the design too (`bag.js`'s
          // `CloseOutScreen`, mounted by both harnesses with its own back
          // action), not a card on the show. Reached from the Live screen's
          // fourth action and from its bag card; contextual off the show, never
          // a rail item — a rail entry needing an `:eventId` is incoherent.
          { path: 'shows/:eventId/bag', element: withSuspense(<ShowBagPage />) },
          // Show mode used to be a separate subpage at this path. Per the Shows
          // detail conformance pass, EventDetailPage is now one screen with three
          // modes off `event.status` — Live is simply what it renders when
          // status='active' (docs/redesign-rulings.md Ruling 5 + §7A "D6"/"D7").
          // Old bookmarks to the subpage redirect rather than 404 — vendor is
          // live and booth phones have this bookmarked (§1.3).
          { path: 'shows/:eventId/show', element: <ShowModeRedirect /> },
          { path: 'shows/report', element: withSuspense(<EventsReportPage />) },
          // Legacy /events/* paths — vendor is live and these are real bookmarks,
          // so every one redirects to its /shows/* equivalent rather than 404ing.
          // navConfig.tsx's nav link still points at `/events` (a concurrent
          // change owns that file — see the Shows conformance PR) and lands here.
          { path: 'events', element: <Navigate to="/shows" replace /> },
          { path: 'events/calendar', element: <Navigate to="/shows" replace /> },
          { path: 'events/:eventId', element: <EventDetailRedirect /> },
          { path: 'events/:eventId/show', element: <ShowModeRedirect /> },
          { path: 'events/report', element: <Navigate to="/shows/report" replace /> },
          { path: 'help', element: withSuspense(<HelpPage />) },
          { path: 'help/:slug', element: withSuspense(<HelpArticlePage />) },
          { path: 'settings', element: withSuspense(<SettingsHome />) },
          // Settings owner-gating moved FROM the route (`ownerOnly`/`RequireRole`,
          // which rendered `ForbiddenPage`) INTO each page via
          // `SettingsPageShell`'s `locked` prop + `OwnerOnlyPage` — the pattern's
          // "never a 403 dressed as a crash" (notes.md §5, vendor#356: "the
          // owner-only page replaces ForbiddenPage"). All five routes below are
          // therefore plain `withSuspense`, same as any other settings route; the
          // owner check now happens once, inside `SettingsPageShell`, not twice.
          { path: 'settings/billing', element: withSuspense(<PlanAndBillingPage />) },
          // The account's PSA key (vendor#1188) — owner-only, matching billing.
          { path: 'settings/psa', element: withSuspense(<PsaSettingsPage />) },
          // The Card Ladder deep-link opt-in (vendor#1247/#1249) — owner-only,
          // matching psa/billing. Without this route the setting has no UI and
          // ships permanently off.
          { path: 'settings/card-ladder', element: withSuspense(<CardLadderSettingsPage />) },
          // vendor#1261's backfill for cards already in inventory. Not
          // owner-gated, same reasoning as settings/slab-links below: it
          // fills a NULL column on rows any seat can already edit.
          {
            path: 'settings/card-ladder-backfill',
            element: withSuspense(<CardLadderBackfillPage />),
          },
          // Corrected 2026-08-12: this comment used to say barcode was the one
          // route here the server does not also enforce (#191). It does now —
          // `set_barcode_sequence()` (#360, PR #775) is the only write path;
          // `authenticated` holds SELECT only on `barcode_sequences` itself, so a
          // direct upsert (what `useUpdateBarcodePrefix` used to do) is refused by
          // the database for every role. See `useBarcodeSequence.ts` (#773).
          { path: 'settings/barcode', element: withSuspense(<BarcodeSequencePage />) },
          // §3.1 decided deliberately that operators MAY write pricing rules — a
          // day-to-day operating surface, not a billing one — and the RLS policies
          // implement exactly that.
          { path: 'settings/pricing', element: withSuspense(<PricingRulesPage />) },
          // Grading service levels (vendor#1188) — plain member gate, matching
          // pricing/spread above. See registry.ts's entry for the RLS citation.
          { path: 'settings/grading-costs', element: withSuspense(<GradingCostsPage />) },
          // Same class as pricing rules, not account administration — vendor#180/
          // #381's Decision B, `docs/multi-seat-accounts.md` §6.1. All four
          // `spread_settings_member_*` RLS policies are the plain member gate
          // (no role predicate), pinned in `supabase/tests/rls/spread_config.sql`.
          { path: 'settings/spread', element: withSuspense(<SpreadSettingsPage />) },
          // vendor#1144's backfill. Not owner-gated: it fills a NULL column on
          // inventory rows any seat can already edit, and writes nothing else.
          { path: 'settings/slab-links', element: withSuspense(<SlabLinksPage />) },
          { path: 'settings/appearance', element: withSuspense(<AppearanceSettingsPage />) },
          { path: 'settings/kiosk', element: withSuspense(<KioskSettingsPage />) },
          { path: 'settings/kiosk-display', element: withSuspense(<KioskDisplaySettingsPage />) },
          // The want list the kiosk's "Cards we're looking for" screen renders
          // (vendor#428). Not owner-gated: `want_list_items_member_*` is the plain
          // member gate, so whoever is working the booth can add what a customer
          // just asked about.
          { path: 'settings/want-list', element: withSuspense(<WantListPage />) },
          // Kiosk leads (vendor#891). Not owner-gated, matching its registry entry
          // and the account-scoped RLS behind it — see that entry's comment on why
          // `kiosk_leads_owner_*` is an account gate despite the name.
          { path: 'settings/kiosk-leads', element: withSuspense(<KioskLeadsPage />) },
          // `settings/intake` REMOVED 2026-08-17 (vendor#136 item 6, owner ruling).
          // Its only control promised that cards below a price threshold arrive as
          // a shared fungible stack — a distinction the storage model dropped with
          // the SKU-stack tables. The submit path never read the toggle, so the
          // threshold changed nothing. Deliberately NOT left as a redirect: there
          // is no successor page to send a bookmark to, and a redirect to
          // `/settings` would be indistinguishable from the 404 the settings shell
          // already renders honestly.
          // The `member` scope (vendor#883) — yours on this account, not the
          // account's and not this browser's. Plain `withSuspense` and NOT
          // owner-gated in any form: there is no owner override to gate, because
          // the RLS policies on `member_settings` are `user_id = auth.uid()` for
          // every write. The owner edits their own row here like anyone else.
          { path: 'settings/you', element: withSuspense(<YourPreferencesPage />) },
          // Team (vendor#58) is NOT page-level owner-gated like the six routes
          // above — the whole page renders for both roles, only the actions
          // differ (TeamPage passes locked={false} always). See registry.ts.
          { path: 'settings/team', element: withSuspense(<TeamPage />) },
          ...prototypeRoutes,
          ...devRoutes,
        ],
      },
    ],
  },
  {
    path: '/kiosk',
    element: withSuspense(<KioskLayout />),
    children: [
      { index: true, element: withSuspense(<KioskHome />) },
      { path: 'cases', element: withSuspense(<KioskDisplayCasesPage />) },
      { path: 'cases/:caseId', element: withSuspense(<KioskCaseDetailPage />) },
      { path: 'binders', element: withSuspense(<KioskBindersPage />) },
      { path: 'binders/:binderId', element: withSuspense(<KioskBinderDetailPage />) },
      { path: 'sealed', element: withSuspense(<KioskSealedPage />) },
      { path: 'bulk-bins', element: withSuspense(<KioskBulkBinsPage />) },
      { path: 'bulk-bins/:binId', element: withSuspense(<KioskBulkBinDetailPage />) },
      { path: 'search', element: withSuspense(<KioskSearchPage />) },
      { path: 'featured', element: withSuspense(<KioskFeaturedView />) },
      { path: 'shows', element: withSuspense(<KioskShowsView />) },
      { path: 'connect', element: withSuspense(<KioskConnectView />) },
      { path: 'pay', element: withSuspense(<KioskPayView />) },
      { path: 'join', element: withSuspense(<KioskJoinView />) },
      { path: 'wanted', element: withSuspense(<KioskWantedPage />) },
      // Catch-all for any /kiosk/* path that doesn't match a child above.
      // It used to be `/kiosk/wanted`'s only destination — KioskHome's
      // `wanted` tile linked there (vendor#774) with no screen mounted — but
      // vendor#428 shipped that route on the line above, so the catch-all is
      // now a pure safety net with no known caller falling into it. Keep it:
      // the reasoning below is about the whole class, not that one tile.
      // Without this, an unmatched /kiosk/* URL falls all the way to this
      // file's top-level
      // `{ path: '*' }` catch-all, rendering <NotFound/> OUTSIDE
      // <KioskLayout> — no kiosk chrome, and NotFound's only affordance is a
      // real `<a href="/">` document navigation. Because the kiosk device
      // keeps the real staff session alive (not anonymous — vendor#742),
      // that document nav lands the customer on the actual authenticated
      // staff dashboard, bypassing KioskHome's 5-tap-corner + PIN + lockout
      // exit gate entirely (security-reviewer finding on vendor#788, High).
      // Redirecting back to /kiosk instead keeps any stray /kiosk/* URL
      // inside the shell, which closes the whole class of "an unmounted
      // kiosk path escapes the boundary" rather than just this one tile.
      // KioskLayout's own `!activeKiosk` guard still runs first and takes
      // priority (no active kiosk session ⇒ `<Navigate to="/" replace />`
      // before this route tree is even reached), so this adds no new way
      // in — only a way to stay inside once already in.
      { path: '*', element: <Navigate to="/kiosk" replace /> },
    ],
  },
  // vendor#137, FINAL TRANCHE (2026-08-18). `/m` is no longer a shell, a
  // layout or a destination — it is a redirect table and nothing else. There
  // is no `MobileLayout`, no bottom nav, no `RequireAuth` wrapper here: every
  // child hands off to a path under `/`, which runs the real guards on
  // arrival. Wrapping a redirect in `RequireAuth` would only mean a
  // signed-out bookmark bounced to `/sign-in` and lost where it was going.
  //
  // These stay mounted rather than 404ing because vendor is LIVE and booth
  // phones have `/m/*` on their home screens. A redirect is for bookmarks; no
  // in-app link points here any more, and
  // `src/__tests__/singleAuthenticatedShell.test.tsx` pins that to zero so the
  // tree cannot regrow callers.
  {
    path: '/m',
    // Kiosk-locked like every other staff entry (vendor#1420). Most of this
    // table only redirects into `/`, which is guarded anyway, but
    // `edit/:barcode` resolves the barcode through a staff RPC BEFORE it
    // redirects. That is a staff read, so it must not run while locked.
    element: (
      <KioskLockGuard>
        <Outlet />
      </KioskLockGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/" replace /> },
      // Chrome, deleted with the shell — each had a desktop equivalent already.
      // Home was the active-show card plus `QuickActionsWidget` (a shared
      // component the `/` dashboard also renders); More was a launcher list
      // into routes the nav rail already carries.
      { path: 'home', element: <Navigate to="/" replace /> },
      { path: 'more', element: <Navigate to="/" replace /> },
      // Moved, not deleted — see the lazy-import block above for why each of
      // these three was the app's only surface for what it does.
      { path: 'search', element: <Navigate to="/search" replace /> },
      { path: 'lookup', element: <Navigate to="/scan" replace /> },
      { path: 'scan', element: <Navigate to="/scan" replace /> },
      { path: 'graded', element: <Navigate to="/graded" replace /> },
      // RETIRED (vendor#384, under vendor#137). The scan-to-edit page is
      // replaced by `/instances/:instanceId/edit`, which is keyed on the
      // instance rather than a nullable barcode. This resolves the one to the
      // other rather than 404ing — a scanner pointed at a printed label is
      // the realistic caller, and vendor is live.
      { path: 'edit/:barcode', element: withSuspense(<BarcodeEditRedirect />) },
      // RETIRED (vendor#227, under vendor#137). The `/m/expenses*` twins are
      // gone: their responsive replacement shipped with the money conformance
      // pass, so `/expenses` now IS the phone surface (keypad amount entry,
      // thumb-reachable save, docked sheets, FAB). Both redirects preserve the
      // query string; in-app callers point at `/expenses` direct.
      { path: 'expenses', element: <MobileExpensesRedirect /> },
      { path: 'expenses/new', element: <MobileExpenseFormRedirect /> },
      { path: 'expenses/:expenseId/edit', element: <MobileExpenseFormRedirect /> },
      // RETIRED (vendor#137, owner ruling 2026-08-18). The booth quick flows
      // and the phone show hub are DELETED, not ported: `/shows/:eventId` in
      // live mode is already the responsive booth screen (`EventDetailLive` —
      // running tally, Sell/Buy/Trade, intake bin), and the three regular
      // intake forms now record through the offline queue, which is the one
      // thing the quick flows did that the forms could not. Each carries
      // `?eventId=` through, because all three destinations read it.
      { path: 'show', element: <Navigate to="/shows" replace /> },
      { path: 'show/:eventId', element: <MobileShowRedirect /> },
      { path: 'show/:eventId/sell', element: <MobileBoothRedirect to="/sales/new" /> },
      { path: 'sell', element: <MobileBoothRedirect to="/sales/new" /> },
      { path: 'show/:eventId/buy', element: <MobileBoothRedirect to="/acquisitions/new/buy" /> },
      { path: 'buy', element: <MobileBoothRedirect to="/acquisitions/new/buy" /> },
      {
        path: 'show/:eventId/trade',
        element: <MobileBoothRedirect to="/acquisitions/new/trade" />,
      },
      { path: 'trade', element: <MobileBoothRedirect to="/acquisitions/new/trade" /> },
      // Anything else under /m — a route this table forgot, or one that never
      // existed — lands on the app rather than a 404, for the same
      // live-bookmarks reason.
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
  // NotFound's only affordance is a document navigation to `/`; while locked,
  // send a stray URL straight back to the kiosk instead (vendor#1420).
  {
    path: '*',
    element: (
      <KioskLockGuard>
        <NotFound />
      </KioskLockGuard>
    ),
  },
]);
